import { useState, useCallback } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useWallet } from '@solana/wallet-adapter-react'
import {
  isConnectWalletDialogOpenVar,
  isConnectWalletOpenVar
} from 'features/auth/store/dialogs'
import { WalletStateStatus } from 'shared/constants'
import { userVar } from 'shared/store/user'
import { wizardModeVar } from '../../features/auth/store/wizard.store'
import { WizardMode } from '../../features/auth/types'
import {
  WalletStateContextProvider,
  type WalletState
} from './wallet-state-context'

const { signInToPlay, connectWallet } = WalletStateStatus

function getTitle(user: boolean, isConnected: boolean): string | null {
  if (!user) return 'Sign in to Play'
  if (!isConnected) return 'Connect wallet'
  return null
}

function getStatusKey(
  user: boolean,
  isConnected: boolean
): WalletStateStatus | null {
  if (!user) return signInToPlay
  if (!isConnected) return connectWallet
  return null
}

export function SolanaWalletStateProvider({
  children
}: {
  children: React.ReactNode
}) {
  const user = useReactiveVar(userVar)
  const [isLoading, setIsLoading] = useState(false)
  const { connected: isConnected, publicKey } = useWallet()

  const isWalletReady = isConnected && Boolean(publicKey)
  const isChainIdValid = true

  const handlePrepareWallet = useCallback(async () => {
    setIsLoading(true)

    if (!user) {
      isConnectWalletDialogOpenVar(true)
      wizardModeVar(WizardMode.connectWallet)
    }

    if (!isConnected) {
      isConnectWalletOpenVar(true)
    }

    setIsLoading(false)
  }, [user, isConnected])

  const value: WalletState = {
    isReady: isWalletReady,
    isChainIdValid,
    isConnected,
    title: getTitle(Boolean(user), isConnected),
    handlePrepareWallet,
    loading: isLoading,
    walletStateKey: getStatusKey(Boolean(user), isConnected)
  }

  return (
    <WalletStateContextProvider value={value}>
      {children}
    </WalletStateContextProvider>
  )
}
